import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { AttrPath, CategorieUitstroomniveauVSO, Table, UitstroomtypeVSO } from '@cumlaude/metadata';
import { generateCssClassForString } from '@cumlaude/shared-utils';
import { camelCase, groupBy, last, range } from 'lodash-es';
import { Observable } from 'rxjs';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { countRecords, SingleAggregator } from '../../services/aggregation';
import { Level, Path } from '../../services/data-tree';
import {
	BasicFilterExpression,
	DataOptions,
	DataResponse,
	DataService,
	defaultDoorstroomAvailableGroups,
	ExportFilter,
} from '../../services/data.service';
import { att, percOfRow } from '../../services/measures';
import { BarInfo } from '../../services/stacked-bars';
import { DashboardVariant } from '../../services/weergave-opties';
import { ColumnDef, TableModel } from '../../shared/components/table/table/table.model';
import { BarchartTableConfig } from '../../shared/dashboard/barchart-table/barchart-table-config';
import { BarchartTableComponent } from '../../shared/dashboard/barchart-table/barchart-table.component';
import { Attributes, BaseDashboardConfig, getLevelFilters } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { DashboardContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { createMeasureColumn, DataRow } from '../../shared/dashboard/data-tree-table/data-tree-table';
import { PartitionMeasure, VbarchartTableComponent } from '../../shared/dashboard/vbarchart-table/vbarchart-table.component';
import { WeergaveOptieComponent } from '../../shared/components/weergave-optie/weergave-optie.component';
import { FilterName } from '../../services/filter-name';
import { LegendaComponent } from '../../legenda/legenda.component';
import { ToggleButtonsComponent } from '@cumlaude/shared-components-buttons';

export interface EinduitstroomI extends Attributes {
	ds_fun_nm_categorie_uitstroomniveau_vso: CategorieUitstroomniveauVSO;
}

export interface EinduitstroomA extends Attributes {
	vervolgonderwijs: number;
	arbeid: number;
	onbekend: number;
	bekend: number;
}

@Component({
	selector: 'app-einduitstroom',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	templateUrl: './einduitstroom.component.html',
	styleUrls: [],
	imports: [
		DashboardContainerComponent,
		FilterPanelComponent,
		DashboardHeaderComponent,
		BarchartTableComponent,
		FilterBarComponent,
		VbarchartTableComponent,
		WeergaveOptieComponent,
		LegendaComponent,
		ToggleButtonsComponent,
	],
})
export class EinduitstroomComponent extends BarchartTableConfig<EinduitstroomI, EinduitstroomA> {
	private readonly dataService = inject(DataService);

	override availableGroups: AttrPath[] = [
		...defaultDoorstroomAvailableGroups,
		['ds_fk_lb_van', 'lb_nm_uitstroomniveau_vso'],
		['ds_fk_lb_van', 'lb_nm_beoogd_uitstroomniveau_vso'],
		['ds_fk_ll', 'll_des_uitschrijving'],
	];

	historieGroups = computed(() => this.groeperingService.huidigeGroeperingen().slice(0, -1));
	historieSubgroups = computed(() => {
		return [...this.groeperingService.huidigeGroeperingen().slice(-1), ['ds_nm_schooljaar_van'] as AttrPath, ...this.subgroups];
	});

	readonly subgroups: AttrPath[] = [['ds_fun_nm_categorie_uitstroomniveau_vso']];

	readonly actueelFilters: FilterName[] = [
		'x_prestatieanalyse_ds_schooljaar',
		'ds_fk_br_vest_van.br_co_brin',
		'ds_fk_vs_van.vs_nm_vestiging',
		'ds_fk_ilt_van.ilt_nm_niveau',
		'ds_nr_leerjaar_van',
		'ds_nm_prestatieanalyse_vso_uitzondering',
		'ds_fk_ll.ll_des_uitschrijving',
	];

	readonly historieFilters: FilterName[] = [
		'x_doorstroom_schooljaar_historie', //
		'x_doorstroom_multiselect_schooljaar',
		...this.actueelFilters.slice(1),
	];

	readonly permanentFilterExpressions = [
		// Bij VSO speelt 1 oktober geen rol, leerlingen stromen het hele jaar in en uit. We laten dus gegevens zien van de plaatsing waarin de leerling daadwerkelijk uitstroomt.
		new BasicFilterExpression(['ds_is_plaatsing_opeenvolgend'], 1),
		new BasicFilterExpression(['ds_nm_uitstroomtype_vso'], UitstroomtypeVSO.EINDUITSTROOM),
		new BasicFilterExpression(['ds_fk_vs_van', 'vs_is_vso'], 1),
	];

	readonly factTable = Table.fac_ds_doorstroom;

	getData(options: DataOptions): Observable<DataResponse<number[]>> {
		return this.dataService.getDoorstroomData({
			...options,
			r: range(0, this.groeperingService.huidigeAantalGroeperingen() + 1),
		});
	}

	variant = this.qp.signal('variant');

	categorieUitstroomniveauVso = this.qp.signal('categorieuitstroomniveauvso');

	constructor() {
		super();
		effect(() => {
			this.categorieUitstroomniveauVso();
			this.filterService.refresh();
		});
	}

	override isHistorieBatchVariant(): boolean {
		return this.variant() === DashboardVariant.HISTORIE && this.groeperingService.huidigeAantalGroeperingen() > 0;
	}

	protected override singleAggregators: Partial<{
		[ai in keyof EinduitstroomA]: SingleAggregator<EinduitstroomI, EinduitstroomA[ai]>;
	}> = {
		vervolgonderwijs: countRecords(
			(attrs: EinduitstroomI) => attrs.ds_fun_nm_categorie_uitstroomniveau_vso === CategorieUitstroomniveauVSO.VERVOLGONDERWIJS
		),
		arbeid: countRecords((attrs: EinduitstroomI) => attrs.ds_fun_nm_categorie_uitstroomniveau_vso === CategorieUitstroomniveauVSO.ARBEID),
		onbekend: countRecords((attrs: EinduitstroomI) => attrs.ds_fun_nm_categorie_uitstroomniveau_vso === null),
		bekend: countRecords((attrs: EinduitstroomI) => attrs.ds_fun_nm_categorie_uitstroomniveau_vso !== null),
	};

	override partitionBarData(
		rowRoot: Level<EinduitstroomA, number[]>,
		_context: DashboardContext<EinduitstroomI, EinduitstroomA, BaseDashboardConfig<EinduitstroomI, EinduitstroomA>>
	): Path<EinduitstroomA, number[]>[][] {
		const { vervolgonderwijs, arbeid, onbekend } = groupBy(rowRoot.r, (r) => camelCase(last(r)?.k ?? 'Onbekend'));
		return [vervolgonderwijs, arbeid, onbekend].filter((records) => records?.length);
	}

	override makeBar(
		attrs: EinduitstroomI,
		path: Path<EinduitstroomA, number[]>,
		context: DashboardContext<EinduitstroomI, EinduitstroomA, BaseDashboardConfig<EinduitstroomI, EinduitstroomA>>
	): BarInfo {
		const categorie = this.displayService.display(attrs.ds_fun_nm_categorie_uitstroomniveau_vso, ['ds_fun_nm_categorie_uitstroomniveau_vso']);
		return {
			...super.makeBar(attrs, path, context),
			text: this.variant() === DashboardVariant.ACTUEEL ? categorie : undefined,
			tooltip: `${categorie}: ${attrs.count_records}`,
			className: generateCssClassForString(categorie),
		};
	}

	override createMeasureColumns(
		context: DashboardContext<EinduitstroomI, EinduitstroomA, EinduitstroomComponent>
	): ColumnDef<DataRow<EinduitstroomA>>[] {
		if (this.variant() === DashboardVariant.HISTORIE) return [];

		const columns = [];
		switch (this.categorieUitstroomniveauVso()) {
			case CategorieUitstroomniveauVSO.VERVOLGONDERWIJS:
				columns.push(
					this.createLeerlingColumn(
						'% vervolgonderwijs',
						percOfRow('vervolgonderwijs', 'bekend'),
						{ context, dataType: 'percentage' },
						undefined,
						[new BasicFilterExpression(['ds_fun_nm_categorie_uitstroomniveau_vso'], CategorieUitstroomniveauVSO.VERVOLGONDERWIJS)]
					),
					this.createLeerlingColumn('Vervolgonderwijs', att('vervolgonderwijs'), { context }, undefined, [
						new BasicFilterExpression(['ds_fun_nm_categorie_uitstroomniveau_vso'], CategorieUitstroomniveauVSO.VERVOLGONDERWIJS),
					])
				);
				break;
			case CategorieUitstroomniveauVSO.ARBEID:
				columns.push(
					this.createLeerlingColumn('% arbeid', percOfRow('arbeid', 'bekend'), { context, dataType: 'percentage' }, undefined, [
						new BasicFilterExpression(['ds_fun_nm_categorie_uitstroomniveau_vso'], CategorieUitstroomniveauVSO.ARBEID),
					]),
					this.createLeerlingColumn('Arbeid', att('arbeid'), { context }, undefined, [
						new BasicFilterExpression(['ds_fun_nm_categorie_uitstroomniveau_vso'], CategorieUitstroomniveauVSO.ARBEID),
					])
				);
				break;
		}

		return [
			...columns,
			this.createLeerlingColumn('Leerlingen', att('bekend'), { context }, undefined, [
				new BasicFilterExpression(['ds_fun_nm_categorie_uitstroomniveau_vso'], null, '<>'),
			]),
			createMeasureColumn('Onbekend', att('onbekend'), { context, clickHandler: (path) => this.handleUitzonderingRedirect(path, context) }),
		];
	}

	protected handleUitzonderingRedirect(
		path: Path<EinduitstroomA, number[]>,
		context: DashboardContext<EinduitstroomI, EinduitstroomA, EinduitstroomComponent>
	) {
		const params = {
			from: this.urlService.getFrom(),
		};

		for (const levelFilter of getLevelFilters(context, path)) {
			Object.assign(params, this.filterService.encodeFilterValue(<FilterName>levelFilter.attr.join('.'), [levelFilter.val]));
		}

		this.urlService.redirectDoorklik([this.getUitzonderingUrl()], params);
	}

	getUitzonderingUrl(): string {
		return '/details/uitzondering/einduitstroom';
	}

	partitionMeasure = computed<PartitionMeasure<EinduitstroomA>>(() => {
		let attribute: keyof EinduitstroomA;
		switch (this.categorieUitstroomniveauVso()) {
			case CategorieUitstroomniveauVSO.VERVOLGONDERWIJS:
				attribute = 'vervolgonderwijs';
				break;
			case CategorieUitstroomniveauVSO.ARBEID:
				attribute = 'arbeid';
				break;
		}
		return {
			type: 'percentage',
			getValue: percOfRow(attribute, 'bekend'),
		};
	});

	protected readonly DashboardVariant = DashboardVariant;

	override enrichTableModel(
		_context: DashboardContext<EinduitstroomI, EinduitstroomA, EinduitstroomComponent>,
		tableModel: TableModel<DataRow<EinduitstroomA>>
	) {
		tableModel.showFooters = this.variant() === DashboardVariant.ACTUEEL;
	}

	protected readonly CategorieUitstroomniveauVSO = CategorieUitstroomniveauVSO;

	override getDisplayOptions(): ExportFilter[] {
		return this.variant() === DashboardVariant.HISTORIE ? [{ label: 'Weergave', value: this.categorieUitstroomniveauVso() }] : [];
	}
}
